import React, {
	ReactNode,
	Ref,
	forwardRef,
	useEffect,
	useId,
	useImperativeHandle,
	useMemo,
	useRef,
	useState,
} from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { Dropdown as DropdownOriginal, DropdownProps as DropdownPropsOriginal } from 'primereact/dropdown';
import { IconType, classNames } from 'primereact/utils';
import { SelectItem } from 'primereact/selectitem';
import { Tooltip } from 'components/_new/Tooltip/Tooltip';
import { getInputBaseClassName } from '../InputBase';
import './Dropdown.scss';

const getDropdownItemId = (value: DropdownItem['value'], label: string, suffix: string) =>
	`dropdown-item-${String(value ?? label)
		.toLowerCase()
		.replace(/[^a-z0-9]+/g, '-')}-${suffix}`;

export type DropdownItem = {
	label: string;
	value: string | number | boolean | null;
	className?: string;
	icon?: IconType<SelectItem>;
	title?: string;
	disabled?: boolean;
	tooltip?: string;
	info?: string;
	data?: Record<string, any>;
};

type DropdownProps = Omit<DropdownPropsOriginal, 'options' | 'dropdownIcon' | 'optionLabel' | 'optionValue'> & {
	options?: DropdownItem[];
	inline?: boolean;
	withEmpty?: boolean;
	emptyLabel?: string;
	emptyClassName?: string | (() => string);
	emptyValue?: any;
	slim?: boolean;
	minimal?: boolean;
	variant?: 'default' | 'primary-outlined';
	invalid?: boolean;
};

type DropdownInfoTooltip = {
	content: string;
	top: number;
	left: number;
	placement: 'right' | 'left' | 'top' | 'bottom';
} | null;

export const Dropdown = forwardRef(
	(
		{
			className,
			value,
			onChange,
			defaultValue,
			inline,
			options,
			withEmpty = false,
			placeholder,
			emptyLabel,
			emptyClassName,
			emptyValue = null,
			'aria-label': ariaLabel,
			itemTemplate,
			onHide,
			onMouseDown,
			onShow,
			panelClassName,
			valueTemplate,
			slim = false,
			minimal = false,
			variant = 'default',
			invalid = false,
			...restProps
		}: DropdownProps & { 'aria-label'?: string },
		ref: Ref<DropdownOriginal>
	) => {
		const { t } = useTranslation();
		const dropdownRef = useRef<DropdownOriginal>(null);
		const [isPanelOpen, setIsPanelOpen] = useState(false);
		const [infoTooltip, setInfoTooltip] = useState<DropdownInfoTooltip>(null);
		const dropdownPanelClassName = `dropdown-panel-${useId().replace(/:/g, '')}`;
		const [currentValueTemp, setCurrentValue] = useState<string | number | null>(
			typeof value === 'undefined' ? defaultValue : value
		);
		const currentValue = currentValueTemp || -1;

		useImperativeHandle(ref, () => {
			const original = dropdownRef.current;
			if (!original) {
				return {} as DropdownOriginal;
			}
			(original as any).focus = () => {
				const focusInput = original.getFocusInput?.();
				if (focusInput) {
					focusInput.focus();
					focusInput.scrollIntoView?.({ behavior: 'smooth', block: 'center' });
				} else {
					const el = original.getElement?.();
					el?.focus?.();
					el?.scrollIntoView?.({ behavior: 'smooth', block: 'center' });
				}
			};
			return original;
		});

		useEffect(() => {
			if (value !== currentValue) {
				setCurrentValue(value);
			}
		}, [value, currentValue]);

		const emptyOption = useMemo(
			() => ({
				value: emptyValue,
				label: emptyLabel || t('misc.dropdownPleaseSelect') || '',
				className: typeof emptyClassName === 'function' ? emptyClassName() : emptyClassName,
			}),
			[emptyValue, emptyLabel, emptyClassName, t]
		);
		const finalOptions: DropdownItem[] = useMemo(
			() => (withEmpty ? [emptyOption, ...(options || [])] : options || []),
			[withEmpty, emptyOption, options]
		);

		const defaultValueTemplate = (item: DropdownItem | null): ReactNode => {
			if (item) {
				const optiondId = `item-${item?.value || item?.label}`;
				return (
					<>
						{item?.tooltip && <Tooltip target={`#${optiondId}`} content={item.tooltip} position="bottom" />}
						<span id={optiondId}>{item.label}</span>
					</>
				);
			}

			return placeholder || emptyLabel || t('misc.dropdownPleaseSelect');
		};

		const hideInfoTooltip = () => {
			setInfoTooltip(null);
		};

		const calculateTooltipPosition = (target: HTMLElement, content: string): NonNullable<DropdownInfoTooltip> => {
			const rect = target.getBoundingClientRect();
			const viewportWidth = window.innerWidth;
			const viewportHeight = window.innerHeight;
			const spaceRight = viewportWidth - rect.right;
			const spaceLeft = rect.left;
			const tooltipMaxWidth = Math.min(340, viewportWidth - 32);

			// On desktop/large screens, if there is plenty of room on the right
			if (viewportWidth >= 768 && spaceRight >= tooltipMaxWidth + 20) {
				return {
					content,
					top: Math.max(16, Math.min(viewportHeight - 16, rect.top + rect.height / 2)),
					left: rect.right + 10,
					placement: 'right',
				};
			}

			// On desktop/large screens, if there is room on the left
			if (viewportWidth >= 768 && spaceLeft >= tooltipMaxWidth + 20) {
				return {
					content,
					top: Math.max(16, Math.min(viewportHeight - 16, rect.top + rect.height / 2)),
					left: rect.left - 10,
					placement: 'left',
				};
			}

			// On mobile / narrow screens: position above or below, centered within screen bounds
			const isAbove = rect.top >= 100;
			const halfWidth = tooltipMaxWidth / 2;
			const targetCenter = rect.left + rect.width / 2;
			const clampedCenterX = Math.max(halfWidth + 16, Math.min(viewportWidth - halfWidth - 16, targetCenter));

			return {
				content,
				top: isAbove ? rect.top - 8 : rect.bottom + 8,
				left: clampedCenterX,
				placement: isAbove ? 'top' : 'bottom',
			};
		};

		const showInfoTooltipForTarget = (target: HTMLElement | null) => {
			const content = target?.getAttribute('data-dropdown-info');
			if (!target || !content) {
				hideInfoTooltip();
				return;
			}

			setInfoTooltip(calculateTooltipPosition(target, content));
		};

		const toggleInfoTooltipForTarget = (target: HTMLElement | null) => {
			const content = target?.getAttribute('data-dropdown-info');
			if (!target || !content) {
				hideInfoTooltip();
				return;
			}

			setInfoTooltip((prev) => {
				if (prev && prev.content === content) {
					return null;
				}
				return calculateTooltipPosition(target, content);
			});
		};

		const defaultItemTemplate = (item: DropdownItem): ReactNode => (
			<div className="dropdown-option-content">
				<span className="dropdown-option-label">{item.label}</span>
				{item.info && (
					<span
						id={getDropdownItemId(item.value, item.label, 'info')}
						className="dropdown-option-info"
						role="button"
						tabIndex={0}
						aria-label={item.info}
						data-dropdown-info={item.info}
						onMouseEnter={(event) => showInfoTooltipForTarget(event.currentTarget)}
						onMouseLeave={hideInfoTooltip}
						onMouseDown={(event) => {
							event.preventDefault();
							event.stopPropagation();
						}}
						onTouchStart={(event) => {
							event.stopPropagation();
						}}
						onTouchEnd={(event) => {
							event.preventDefault();
							event.stopPropagation();
							toggleInfoTooltipForTarget(event.currentTarget);
						}}
						onClick={(event) => {
							event.preventDefault();
							event.stopPropagation();
							toggleInfoTooltipForTarget(event.currentTarget);
						}}
						onKeyDown={(event) => {
							if (event.key === 'Enter' || event.key === ' ') {
								event.preventDefault();
								event.stopPropagation();
								toggleInfoTooltipForTarget(event.currentTarget);
							}
						}}
					>
						<i className="fa fa-info-circle" aria-hidden="true" />
					</span>
				)}
			</div>
		);

		const getOverlay = () => dropdownRef.current?.getOverlay();
		const getRoot = () => dropdownRef.current?.getElement();
		const getFocusableItems = () =>
			Array.from(getOverlay()?.querySelectorAll('.p-dropdown-item:not(.p-disabled)') || []) as HTMLElement[];

		const showHighlightedItemTooltip = () => {
			const overlay = dropdownRef.current?.getOverlay();
			const focusedItem = overlay?.querySelector('.p-dropdown-item:focus') as HTMLElement | null;
			const target = focusedItem?.querySelector('.dropdown-option-info') as HTMLElement | null;

			if (!focusedItem || !target) {
				hideInfoTooltip();
				return;
			}

			showInfoTooltipForTarget(target);
		};

		const hideDropdown = () => {
			setIsPanelOpen(false);
			hideInfoTooltip();
			(dropdownRef.current as any)?.hide?.();
		};

		useEffect(() => {
			if (!isPanelOpen) {
				return undefined;
			}

			const focusInput = dropdownRef.current?.getFocusInput();
			const overlay = getOverlay();
			const root = getRoot();
			if (!focusInput || !overlay) {
				return undefined;
			}

			const focusItemAtIndex = (index: number, options?: { showTooltip?: boolean }) => {
				const items = getFocusableItems();
				const target = items[index];
				if (!target) {
					return;
				}

				items.forEach((item) => item.setAttribute('tabindex', '-1'));
				target.setAttribute('tabindex', '0');
				target.focus();
				if (options?.showTooltip) {
					showHighlightedItemTooltip();
				} else {
					hideInfoTooltip();
				}
			};

			const focusFirstItem = () => {
				const items = getFocusableItems();
				if (!items.length) {
					return;
				}

				focusItemAtIndex(0, { showTooltip: false });
			};

			setTimeout(focusFirstItem, 0);

			const handleOverlayKeyDown = (event: KeyboardEvent) => {
				const items = getFocusableItems();
				const activeElement = document.activeElement as HTMLElement | null;
				const currentIndex = activeElement ? items.indexOf(activeElement) : -1;

				if (event.key === 'ArrowDown') {
					event.preventDefault();
					focusItemAtIndex(Math.min(currentIndex + 1, items.length - 1), { showTooltip: true });
					return;
				}

				if (event.key === 'ArrowUp') {
					event.preventDefault();
					focusItemAtIndex(Math.max(currentIndex - 1, 0), { showTooltip: true });
					return;
				}

				if (event.key === 'Home') {
					event.preventDefault();
					focusItemAtIndex(0, { showTooltip: true });
					return;
				}

				if (event.key === 'End') {
					event.preventDefault();
					focusItemAtIndex(items.length - 1, { showTooltip: true });
					return;
				}

				if (event.key === 'Enter' || event.key === ' ') {
					event.preventDefault();
					activeElement?.click();
					return;
				}

				if (event.key === 'Escape') {
					event.preventDefault();
					hideDropdown();
					focusInput.focus();
					return;
				}

				if (event.key === 'Tab') {
					hideDropdown();
				}
			};

			const handleFocusOut = () => {
				setTimeout(() => {
					const activeElement = document.activeElement as HTMLElement | null;
					const nextInsideDropdown =
						Boolean(activeElement && overlay.contains(activeElement)) ||
						Boolean(activeElement && root?.contains(activeElement));

					if (!nextInsideDropdown) {
						hideDropdown();
					}
				}, 0);
			};

			overlay.addEventListener('keydown', handleOverlayKeyDown);
			overlay.addEventListener('focusout', handleFocusOut);
			root?.addEventListener('focusout', handleFocusOut);

			return () => {
				overlay.removeEventListener('keydown', handleOverlayKeyDown);
				overlay.removeEventListener('focusout', handleFocusOut);
				root?.removeEventListener('focusout', handleFocusOut);
			};
			// eslint-disable-next-line react-hooks/exhaustive-deps
		}, [isPanelOpen]);

		useEffect(() => {
			const root = getRoot();
			if (!root) {
				return undefined;
			}

			const handleRootMouseDownCapture = (event: MouseEvent) => {
				const overlay = getOverlay();
				const target = event.target as HTMLElement | null;
				const clickedInsideOverlay = Boolean(target && overlay?.contains(target));
				const clickedInfoIcon = Boolean(target?.closest('.dropdown-option-info'));

				if (isPanelOpen && !clickedInsideOverlay && !clickedInfoIcon) {
					event.preventDefault();
					event.stopPropagation();
					(event as any).stopImmediatePropagation?.();
					hideDropdown();
				}
			};

			root.addEventListener('mousedown', handleRootMouseDownCapture, true);

			return () => {
				root.removeEventListener('mousedown', handleRootMouseDownCapture, true);
			};
		}, [isPanelOpen]);

		return (
			<>
				<DropdownOriginal
					ref={dropdownRef}
					value={currentValue}
					defaultValue={defaultValue}
					aria-label={ariaLabel}
					onChange={(event) => {
						if (onChange) {
							onChange(event);
						} else {
							setCurrentValue(event.value);
						}
					}}
					onMouseDown={(event) => {
						onMouseDown?.(event);
					}}
					onShow={() => {
						setIsPanelOpen(true);
						onShow?.();
					}}
					onHide={() => {
						setIsPanelOpen(false);
						hideInfoTooltip();
						onHide?.();
					}}
					valueTemplate={valueTemplate || defaultValueTemplate}
					itemTemplate={itemTemplate || defaultItemTemplate}
					className={classNames(
						getInputBaseClassName({ inline, slim, variant, minimal, invalid }),
						'dropdown',
						className
					)}
					dropdownIcon="fa fa-solid fa-chevron-down"
					panelClassName={classNames('dropdown-panel', panelClassName, dropdownPanelClassName)}
					optionLabel="label"
					optionValue="value"
					{...restProps}
					options={finalOptions}
				/>
				{isPanelOpen &&
					infoTooltip &&
					typeof document !== 'undefined' &&
					createPortal(
						<div
							className={classNames(
								'dropdown-option-tooltip',
								`dropdown-option-tooltip-${infoTooltip.placement}`
							)}
							role="tooltip"
							style={{
								top: `${infoTooltip.top}px`,
								left: `${infoTooltip.left}px`,
							}}
						>
							{infoTooltip.content}
						</div>,
						document.body
					)}
			</>
		);
	}
);
